import React, { useState } from 'react';
import { Stock, Order } from '../../types';

import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { Select } from '../atoms/Select';


interface OrderFormData {
  type: 'market' | 'limit';
  quantity: number;
  price: number;
}

interface OrderFormProps {
  stock: Stock;
  type: 'buy' | 'sell';
  onSubmit: (order: Omit<Order, 'id' | 'status' | 'timestamp'>) => void;
  onCancel: () => void;
}

export const OrderForm: React.FC<OrderFormProps> = ({ stock, type, onSubmit, onCancel }) => {
  const [orderData, setOrderData] = useState<OrderFormData>({
    type: 'market',
    quantity: 1,
    price: stock?.price || 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      symbol: stock.symbol,
      side: type,
      ...orderData,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="order-form">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {type === 'buy' ? 'Buy' : 'Sell'} {stock?.symbol}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">${stock?.price.toFixed(2)}</p>
      </div>

      <Select
        label="Order Type"
        options={[
          { value: 'market', label: 'Market Order' },
          { value: 'limit', label: 'Limit Order' },
        ]}
        value={orderData.type}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOrderData({ ...orderData, type: e.target.value as 'market' | 'limit' })}
        data-testid="order-type-select"
      />

      <Input
        label="Quantity"
        type="number"
        min="1"
        value={orderData.quantity}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrderData({ ...orderData, quantity: parseInt(e.target.value) })}
        data-testid="quantity-input"
      />

      {orderData.type === 'limit' && (
        <Input
          label="Limit Price"
          type="number"
          step="0.01"
          value={orderData.price}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrderData({ ...orderData, price: parseFloat(e.target.value) })}
          data-testid="price-input"
        />
      )}

      <div className="flex gap-2">
        <Button type="submit" className="flex-1" data-testid="place-order-button">
          Place {type === 'buy' ? 'Buy' : 'Sell'} Order
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} data-testid="cancel-order-button">
          Cancel
        </Button>
      </div>
    </form>
  );
};